import * as React from 'react';
import { Dialog } from 'primereact/dialog';

import { ProjectDocFolder } from '../../models/ProjectDocFolder';
import { AddActDocumentModel } from '../../services/ProjectDocFolderService';
import { NewDropdown } from '../../../../components/synced/ui/NewDropdown/NewDropdown';
import { ApplicationState } from '../../../../store';
import { useSelector } from 'react-redux';
import { DictIntEntity } from '../../../../types/models/base/DictIntEntity';
import { NewInput } from '../../../../components/synced/ui/NewInput/NewInput';
import { NewCalendar } from '../../../../components/synced/ui/NewCalendar/NewCalendar';
import { constantFormat } from '../../../../helpers/constants';
import { NewButton } from '../../../../components/synced/ui/NewButton/NewButton';
import { DocumentTypeVals } from '../../../../helpers/mapping/DocumentTypeVals';
import { DocumentKindVals } from '../../../../helpers/mapping/DocumentKindVals';
import { ProjectDocTypeVals } from '../../../../helpers/mapping/ProjectDocTypes';

interface AddActDocumentDialogProps {
    visible: boolean;
    folder: ProjectDocFolder;
    docTypeId: number;
    editFolder?: ProjectDocFolder;
    onSave: (folder: AddActDocumentModel) => void;
    onSaveEdit?: (folder: ProjectDocFolder) => void;
    onCancel: () => void;
}

export const AddActDocumentDialog: React.FC<AddActDocumentDialogProps> = ({
    visible,
    folder,
    docTypeId,
    editFolder,
    onSave,
    onSaveEdit,
    onCancel
}) => {
    const [data, setData] = React.useState<AddActDocumentModel>({} as AddActDocumentModel);
    React.useEffect(() => {
        if (editFolder) {
            setData({
                ProjectDocFolderId: editFolder.Id,
                ActDocumentTypeId: editFolder.DocumentTypeId ?? 0,
                Title: editFolder.Title ?? '',
                Cypher: editFolder.Cypher ?? '',
                DocumentDate: editFolder.DocumentDate ?? '',
                DocumentDateEnd: editFolder.DocumentDateEnd ?? '',
                DocumentKindId: editFolder.DocumentKindId,
                DocumentKindCustom: editFolder.DocumentKindCustom,
                DocumentOrganization: editFolder.DocumentOrganization ?? ''
            } as AddActDocumentModel);
        } else {
            setData({ ProjectDocFolderId: folder.Id } as AddActDocumentModel);
        }
    }, [folder, editFolder]);

    const dictionaries = useSelector((appState: ApplicationState) => appState.dictionariesContext.dictionariesContext);

    const optionsActDucumentTypes: DictIntEntity[] = React.useMemo<DictIntEntity[]>(
        () => dictionaries.DocumentTypeItems,
        [dictionaries]
    );

    const isExecutive = docTypeId === ProjectDocTypeVals.Executive;
    const isQuality = data.ActDocumentTypeId === DocumentTypeVals.Quality;
    const isDateRange = isQuality && DocumentKindVals.isDateRange(data.DocumentKindId ?? 0);

    const isDataValid = React.useCallback((): boolean => {
        if (!Boolean(data.ActDocumentTypeId)) return false;
        if (!data?.Title?.trim()?.length) return false;
        if (!data?.Cypher?.trim()?.length) return false;
        if (isExecutive && !data?.DocumentOrganization?.trim()?.length) return false;
        if (!Boolean(data?.DocumentDate)) return false;
        if (isQuality) {
            if (!data.DocumentKindId) return false;
            if (data.DocumentKindId === DocumentKindVals.Other && !data.DocumentKindCustom?.trim()) return false;
            if (isDateRange && !data.DocumentDateEnd) return false;
        }
        return true;
    }, [data, isQuality, isExecutive, isDateRange]);

    React.useEffect(() => {
        if (editFolder || !folder || !Boolean(folder?.Id) || folder?.Id === data.ProjectDocFolderId) return;

        let newData = {} as AddActDocumentModel;
        if (folder) {
            newData.ProjectDocFolderId = folder.Id ?? 0;
            if (Boolean(folder.DocumentTypeId)) {
                newData.ActDocumentTypeId = folder.DocumentTypeId ?? 0;
            }
        }
        setData(newData);
    }, [folder, data, setData, editFolder]);

    const footer = (
        <div className="pd-dialog-footer">
            <NewButton variant="secondary" icon="fa-solid fa-xmark" onClick={onCancel}>
                Отменить
            </NewButton>
            <NewButton
                variant="primary"
                icon="fa-solid fa-check"
                disabled={!isDataValid()}
                onClick={() => {
                    if (editFolder && onSaveEdit) {
                        onSaveEdit({
                            ...editFolder,
                            DocumentOrganization: data.DocumentOrganization,
                            DocumentTypeId: data.ActDocumentTypeId,
                            Title: data.Title,
                            Cypher: data.Cypher,
                            DocumentDate: data.DocumentDate,
                            DocumentDateEnd: isDateRange ? data.DocumentDateEnd : undefined,
                            DocumentKindId: isQuality ? data.DocumentKindId : undefined,
                            DocumentKindCustom:
                                isQuality && data.DocumentKindId === DocumentKindVals.Other
                                    ? data.DocumentKindCustom
                                    : undefined,
                            Children: []
                        });
                    } else {
                        onSave(data);
                    }
                }}
            >
                Сохранить
            </NewButton>
        </div>
    );

    return (
        <React.Fragment>
            <Dialog
                style={{ width: '50vw' }}
                footer={footer}
                focusOnShow={false}
                contentStyle={{ overflow: `visible` }}
                className="pd-dialog"
                header={editFolder ? 'Изменение документа' : 'Добавление документа'}
                visible={visible}
                onHide={onCancel}
            >
                <div className="pd-dialog-body">
                    <div className="pd-dialog-field">
                        <span className="pd-dialog-label">Тип документа</span>
                        <NewDropdown
                            className="w-100"
                            disabled={Boolean(folder?.DocumentTypeId)}
                            value={data?.ActDocumentTypeId}
                            options={optionsActDucumentTypes}
                            onChange={(e) => setData({ ...data, ActDocumentTypeId: e.value })}
                            appendTo={document.body}
                            optionLabel="Title"
                            optionValue="Id"
                        />
                        {!data.ActDocumentTypeId && <small className="pd-field-error">Укажите значение</small>}
                    </div>
                    {isQuality && (
                        <>
                            <div className="pd-dialog-field">
                                <span className="pd-dialog-label">Вид документа</span>
                                <NewDropdown
                                    className="w-100"
                                    value={data.DocumentKindId}
                                    options={DocumentKindVals.Items}
                                    optionLabel="Title"
                                    optionValue="Id"
                                    appendTo={document.body}
                                    onChange={(e) =>
                                        setData({ ...data, DocumentKindId: e.value, DocumentKindCustom: undefined })
                                    }
                                />
                                {!data.DocumentKindId && <small className="pd-field-error">Укажите значение</small>}
                            </div>

                            {data.DocumentKindId === DocumentKindVals.Other && (
                                <div className="pd-dialog-field">
                                    <span className="pd-dialog-label">Наименование вида документа</span>
                                    <NewInput
                                        type="text"
                                        className="w-100"
                                        placeholder="Введите наименование"
                                        value={data.DocumentKindCustom}
                                        onChange={(e) =>
                                            setData({ ...data, DocumentKindCustom: e.currentTarget.value })
                                        }
                                    />
                                    {!data.DocumentKindCustom?.trim() && (
                                        <small className="pd-field-error">Укажите значение</small>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                    <div className="pd-dialog-field">
                        <span className="pd-dialog-label">Название</span>
                        <NewInput
                            type="text"
                            className="w-100"
                            placeholder="Введите название"
                            value={data.Title}
                            onChange={(e) => setData({ ...data, Title: e.currentTarget.value })}
                        />
                        {(!data.Title || !data.Title?.trim()) && (
                            <small className="pd-field-error">Укажите значение</small>
                        )}
                    </div>
                    <div className="pd-dialog-field">
                        <span className="pd-dialog-label">{isExecutive ? '№ документа' : 'Номер'}</span>
                        <NewInput
                            type="text"
                            className="w-100"
                            placeholder={isExecutive ? 'Введите № документа' : 'Введите номер'}
                            value={data.Cypher}
                            onChange={(e) => setData({ ...data, Cypher: e.currentTarget.value })}
                        />
                        {(!data.Cypher || !data.Cypher?.trim()) && (
                            <small className="pd-field-error">Укажите значение</small>
                        )}
                    </div>
                    {isExecutive && (
                        <div className="pd-dialog-field">
                            <span className="pd-dialog-label">Организация, предоставившая документ</span>
                            <NewInput
                                type="text"
                                className="w-100"
                                placeholder="Введите организацию"
                                value={data.DocumentOrganization}
                                onChange={(e) => setData({ ...data, DocumentOrganization: e.currentTarget.value })}
                            />
                            {(!data.DocumentOrganization || !data.DocumentOrganization?.trim()) && (
                                <small className="pd-field-error">Укажите значение</small>
                            )}
                        </div>
                    )}
                    {isDateRange ? (
                        <div className="pd-dialog-field">
                            <span className="pd-dialog-label">Срок действия</span>
                            <NewCalendar
                                keepInViewport
                                appendTo={document.body}
                                locale="ru"
                                selectionMode="range"
                                dateFormat={constantFormat.calendar}
                                className="w-100"
                                value={
                                    (() => {
                                        const from = constantFormat.StringToDate(data.DocumentDate as string);
                                        const to = constantFormat.StringToDate(data.DocumentDateEnd as string);
                                        const validFrom = from instanceof Date && !isNaN(from.getTime()) ? from : null;
                                        const validTo = to instanceof Date && !isNaN(to.getTime()) ? to : null;
                                        if (!validFrom) return null;
                                        return validTo ? [validFrom, validTo] : [validFrom];
                                    })() as Date[]
                                }
                                onChange={(e) => {
                                    const range = e.value as Date[];
                                    setData({
                                        ...data,
                                        DocumentDate: range?.[0] ?? '',
                                        DocumentDateEnd: range?.[1] ?? ''
                                    });
                                }}
                            />
                            {(!data.DocumentDate || !data.DocumentDateEnd) && (
                                <small className="pd-field-error">Укажите диапазон дат</small>
                            )}
                        </div>
                    ) : (
                        <div className="pd-dialog-field">
                            <span className="pd-dialog-label">Дата документа</span>
                            <NewCalendar
                                keepInViewport
                                appendTo={document.body}
                                locale="ru"
                                dateFormat={constantFormat.calendar}
                                className="w-100"
                                value={constantFormat.StringToDate(data.DocumentDate as string)}
                                onChange={(e) => setData({ ...data, DocumentDate: e.value as Date })}
                            />
                            {!data.DocumentDate && <small className="pd-field-error">Укажите значение</small>}
                        </div>
                    )}
                </div>
            </Dialog>
        </React.Fragment>
    );
};
